import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  Document,
  Packer,
  Paragraph,
  Table,
  TableRow,
  TableCell,
  TextRun,
  WidthType,
} from "docx";

const filterNodeInfo = (nodeData) => {
  return {
    nodeNumber: nodeData.nodeNumber,
    drawingNumber: nodeData.drawingNumber,
    designIntent: nodeData.designIntent,
    chairman: `${nodeData.chairman?.firstName + " " + nodeData.chairman?.lastName || "N/A"}`,
    scribe: `${nodeData.scribe?.firstName + " " + nodeData.scribe?.lastName || "N/A"}`,
    team: nodeData.team.map((member) => `${member.firstName} ${member.lastName}`).join(", "),
  };
};

const getRiskColorRGB = (risk) => {
  if (risk < 3) return [144, 238, 144]; // light green
  if (risk >= 4 && risk <= 9) return [255, 255, 153]; // yellow
  if (risk >= 10 && risk <= 16) return [255, 165, 0]; // orange
  return [255, 105, 97]; // red
};

const getRiskColorARGB = (risk) => {
  if (risk < 3) return "FF90EE90";
  if (risk >= 4 && risk <= 9) return "FFFFFF99";
  if (risk >= 10 && risk <= 16) return "FFFFA500";
  return "FFFF6961";
};

// PDF Export
export const exportAsPDF = (nodeData, worksheets) => {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  const filteredNodeData = filterNodeInfo(nodeData);

  // Add title
  doc.setFontSize(20);
  doc.setTextColor(59, 130, 246); // Blue color
  doc.text("HAZOP Analysis Report", 20, 20);
  
  // Add node information
  doc.setFontSize(16);
  doc.setTextColor(71, 85, 105); // Slate color
  doc.text("Node Information", 20, 35);
  
  let yPosition = 45;
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  
  Object.entries(filteredNodeData).forEach(([key, value]) => {
    const label = key.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase());
    doc.text(`${label}: ${value}`, 20, yPosition);
    yPosition += 8;
  });

  // Add worksheet table
  doc.setFontSize(16);
  doc.setTextColor(71, 85, 105);
  doc.text("Worksheet Data", 20, yPosition + 10);

  autoTable(doc, {
    startY: yPosition + 20,
    headStyles: { 
      fillColor: [59, 130, 246], 
      textColor: 255, 
      halign: "center",
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3
    },
    head: [["S.No", "Guide Word", "Deviation", "Cause", "Consequence", "Severity", "Likelihood", "Risk", "Safeguard", "Recommendation"]],
    body: worksheets.map((entry, idx) => [
      idx + 1,
      entry.guideWordParameter || "-",
      Array.isArray(entry.deviation) ? entry.deviation.join(", ") : entry.deviation || "-",
      Array.isArray(entry.causes) ? entry.causes.join(", ") : entry.causes || "-",
      Array.isArray(entry.consequences) ? entry.consequences.join(", ") : entry.consequences || "-",
      entry.severity || "-",
      entry.likelihood || "-",
      entry.risk || "-",
      Array.isArray(entry.existingSafeguard) ? entry.existingSafeguard.join(", ") : entry.existingSafeguard || "-",
      Array.isArray(entry.recommendation) ? entry.recommendation.join(", ") : entry.recommendation || "-",
    ]),
    
    didParseCell: (data) => {
      if (data.column.index === 7 && data.cell.raw !== "-" && !isNaN(data.cell.raw)) {
        const risk = Number(data.cell.raw);
        data.cell.styles.fillColor = getRiskColorRGB(risk);
        data.cell.styles.textColor = risk >= 10 ? [255, 255, 255] : [0, 0, 0];
      }
    },
    
    columnStyles: {
      0: { cellWidth: 15 },
      1: { cellWidth: 25 },
      2: { cellWidth: 35 },
      3: { cellWidth: 35 },
      4: { cellWidth: 35 },
      5: { cellWidth: 20 },
      6: { cellWidth: 20 },
      7: { cellWidth: 15 },
      8: { cellWidth: 35 },
      9: { cellWidth: 35 }
    }
  });

  doc.save(`Node-${nodeData.nodeNumber}-HAZOP-Report.pdf`);
};

const getRiskColor = (risk) => {
  if (risk < 3) return "92D050"; // Green
  if (risk <= 9) return "FFFF00"; // Yellow
  if (risk <= 16) return "FFC000"; // Orange
  return "FF0000"; // Red
};

// Word Export
export const exportAsWord = async (nodeData, worksheets) => {
  const filteredNodeData = filterNodeInfo(nodeData);

  const nodeInfoParagraphs = [
    new Paragraph({ 
      text: "HAZOP Analysis Report", 
      heading: "Title",
      spacing: { after: 400 }
    }),
    new Paragraph({ 
      text: "Node Information", 
      heading: "Heading1",
      spacing: { before: 200, after: 200 }
    }),
    ...Object.entries(filteredNodeData).map(([key, value]) =>
      new Paragraph({
        children: [
          new TextRun({ 
            text: `${key.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())}: `, 
            bold: true,
            color: "2563EB"
          }),
          new TextRun({ text: typeof value === "object" ? JSON.stringify(value) : String(value) }),
        ],
        spacing: { after: 100 }
      })
    ),
    new Paragraph({ 
      text: "Worksheet Entries", 
      heading: "Heading1",
      spacing: { before: 400, after: 200 }
    }),
  ];

  const headerRow = new TableRow({
    children: [
      "S.No", "Guide Word", "Deviation", "Cause", "Consequence",
      "Severity", "Likelihood", "Risk", "Safeguard", "Recommendation"
    ].map(header =>
      new TableCell({
        children: [new Paragraph({ 
          text: header, 
          alignment: "center",
          run: { bold: true, color: "FFFFFF" }
        })],
        width: { size: 10, type: WidthType.PERCENTAGE },
        shading: { fill: "3B82F6" }
      })
    ),
  });

  const worksheetRows = worksheets.map((entry, idx) =>
    new TableRow({
      children: [
        new TableCell({ children: [new Paragraph({ text: String(idx + 1), alignment: "center" })] }),
        new TableCell({ children: [new Paragraph(entry.guideWordParameter || "-")] }),
        new TableCell({ children: [new Paragraph(Array.isArray(entry.deviation) ? entry.deviation.join(", ") : entry.deviation || "-")] }),
        new TableCell({
          children: Array.isArray(entry.causes) && entry.causes.length > 0 
            ? entry.causes.map(c => new Paragraph(String(c)))
            : [new Paragraph("-")]
        }),
        new TableCell({
          children: Array.isArray(entry.consequences) && entry.consequences.length > 0 
            ? entry.consequences.map(c => new Paragraph(String(c)))
            : [new Paragraph("-")]
        }),
        new TableCell({ children: [new Paragraph({ text: String(entry.severity || "-"), alignment: "center" })] }),
        new TableCell({ children: [new Paragraph({ text: String(entry.likelihood || "-"), alignment: "center" })] }),
        new TableCell({
          children: [new Paragraph({ text: String(entry.risk || "-"), alignment: "center" })],
          shading: { fill: getRiskColor(entry.risk || 0) }
        }),
        new TableCell({
          children: Array.isArray(entry.existingSafeguard) && entry.existingSafeguard.length > 0
            ? entry.existingSafeguard.map(s => new Paragraph(String(s)))
            : [new Paragraph("-")]
        }),
        new TableCell({
          children: Array.isArray(entry.recommendation) && entry.recommendation.length > 0
            ? entry.recommendation.map(r => new Paragraph(String(r)))
            : [new Paragraph("-")]
        }),
      ],
    })
  );

  const doc = new Document({
    sections: [
      {
        children: [
          ...nodeInfoParagraphs,
          new Table({
            rows: [headerRow, ...worksheetRows],
            width: { size: 100, type: WidthType.PERCENTAGE },
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `Node-${nodeData.nodeNumber}-HAZOP-Report.docx`);
};

// Excel Export
export const exportAsExcel = async (nodeData, worksheets) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("HAZOP Analysis");

  const filteredNodeData = filterNodeInfo(nodeData);

  // Add title
  const titleRow = sheet.addRow(["HAZOP Analysis Report"]);
  titleRow.getCell(1).font = { size: 18, bold: true, color: { argb: "FF3B82F6" } };
  sheet.addRow([]);

  // Add node information
  const nodeInfoRow = sheet.addRow(["Node Information"]);
  nodeInfoRow.getCell(1).font = { size: 14, bold: true, color: { argb: "FF475569" } };
  
  Object.entries(filteredNodeData).forEach(([key, value]) => {
    const row = sheet.addRow([key.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase()), value]);
    row.getCell(1).font = { bold: true };
  });

  sheet.addRow([]);
  const worksheetTitleRow = sheet.addRow(["Worksheet Entries"]);
  worksheetTitleRow.getCell(1).font = { size: 14, bold: true, color: { argb: "FF475569" } };

  const headerRow = sheet.addRow([
    "S.No", "Guide Word", "Deviation", "Cause", "Consequence",
    "Severity", "Likelihood", "Risk", "Safeguard", "Recommendation"
  ]);

  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF3B82F6" },
    };
    cell.font = { color: { argb: "FFFFFFFF" }, bold: true };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  });

  const getRiskColorHex = (risk) => {
    if (risk < 3) return "FF92D050"; // Green
    if (risk <= 9) return "FFFFFF00"; // Yellow
    if (risk <= 16) return "FFFFC000"; // Orange
    return "FFFF0000"; // Red
  };

  worksheets.forEach((entry, idx) => {
    const row = sheet.addRow([
      idx + 1,
      entry.guideWordParameter || "-",
      Array.isArray(entry.deviation) ? entry.deviation.join(", ") : entry.deviation || "-",
      Array.isArray(entry.causes) ? entry.causes.join(", ") : entry.causes || "-",
      Array.isArray(entry.consequences) ? entry.consequences.join(", ") : entry.consequences || "-",
      entry.severity || "-",
      entry.likelihood || "-",
      entry.risk || "-",
      Array.isArray(entry.existingSafeguard) ? entry.existingSafeguard.join(", ") : entry.existingSafeguard || "-",
      Array.isArray(entry.recommendation) ? entry.recommendation.join(", ") : entry.recommendation || "-",
    ]);

    // Style each cell
    row.eachCell((cell, colNumber) => {
      cell.alignment = { wrapText: true, vertical: "top" };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
      
      // Center align numeric columns
      if ([1, 6, 7, 8].includes(colNumber)) {
        cell.alignment = { ...cell.alignment, horizontal: "center" };
      }
    });

    // Apply background color to Risk column (index 8)
    const riskCell = row.getCell(8);
    const riskValue = entry.risk || 0;
    riskCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: getRiskColorHex(riskValue) },
    };
    
    // Set text color for better contrast
    if (riskValue >= 10) {
      riskCell.font = { color: { argb: "FFFFFFFF" }, bold: true };
    } else {
      riskCell.font = { color: { argb: "FF000000" }, bold: true };
    }
  });

  // Adjust column widths for better readability
  sheet.columns = [
    { width: 8 },   // S.No
    { width: 15 },  // Guide Word
    { width: 25 },  // Deviation
    { width: 30 },  // Cause
    { width: 30 },  // Consequence
    { width: 12 },  // Severity
    { width: 12 },  // Likelihood
    { width: 10 },  // Risk
    { width: 30 },  // Safeguard
    { width: 30 },  // Recommendation
  ];

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `Node-${nodeData.nodeNumber}-HAZOP-Report.xlsx`
  );
};